import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  X, Calendar as CalendarIcon, Loader2, ArrowRight, Stethoscope,
  Armchair, ChevronLeft, ChevronRight, AlertTriangle, Check, Clock,
} from "lucide-react";
import { supabase } from "@/lib/supabaseClient";
import { useBranch } from "@/context/BranchContext";
import { defaultBookingWindowDays, bookingWindowHint } from "@/lib/procedureFlags";
import { describeFollowUpDays } from "@/lib/followUpWindow";
import { followUpBookingHeading, followUpRequestedDays } from "@/lib/followUpLabels";
import { isBlockingAppointmentStatus } from "@/lib/appointmentStatus";
import { formatDoctorName } from "@/lib/doctorName";
import {
  CLINIC_TIME_ZONE,
  addClinicDays,
  clinicDateAt,
  clinicDateAtMinutes,
  clinicDateStringFromInstant,
  clinicDayIndex,
  clinicTimePartsFromInstant,
  clinicTodayDateString,
} from "@/lib/clinicTime";

// =====================================================================
// BookFutureNeedDialog — inline appointment booking for Step 3.
//
// The front desk doesn't get bounced to /calendar anymore. Inside this
// modal they:
//   1. Pick a date (smart-defaulted by procedure category urgency)
//   2. Pick doctor + chair (defaulted to today's visit doctor/chair)
//   3. See a row of 15-min time chips for that day
//        - Green = free AND fits the duration
//        - Red   = busy / not enough contiguous time
//        - Selected = the one they clicked
//   4. Click Book → calls public.create_appointment, returns the new
//      appointment_id back via onBooked() so the parent can stage the
//      "link this future need to that appointment" disposition.
//
// Existing-appointment list still appears below as a secondary path
// for "actually I want this on a slot they already have."
// =====================================================================

const ELIGIBLE_STATUSES = ["Scheduled", "Confirmed"];
const TUTORIAL_BOOK_SLOT_TARGET_ID = "book-slot-10-0";
const EMPTY_DEMO_APPOINTMENTS = [];

function tutorialSlotTargetId(slot) {
  if (slot?.h === 10 && slot?.m === 0) return TUTORIAL_BOOK_SLOT_TARGET_ID;
  return `book-slot-${slot.h}-${slot.m}`;
}

// Urgency window is now catalogue-driven (procedures.default_booking
// _window_days). When the bundle exposes full procedure objects, we
// pick the most urgent (smallest days) value. Falls back to the
// legacy category map in @/lib/procedureFlags when the column hasn't
// been populated yet.
function pickUrgencyDays(categoriesOrProcs) {
  if (!categoriesOrProcs || categoriesOrProcs.length === 0) return 10;
  let min = 90;
  let sawPreventive = false;
  for (const item of categoriesOrProcs) {
    let days = null;
    if (typeof item === "string") {
      // Legacy: bundle gives us category names only.
      days = defaultBookingWindowDays({ category: item });
      if (item === "Preventive") sawPreventive = true;
    } else if (item) {
      // New: full procedure objects with the catalog flag.
      days = defaultBookingWindowDays(item);
      if (item.category === "Preventive") sawPreventive = true;
    }
    if (Number.isFinite(days) && days < min) min = days;
  }
  return (min === 90 && sawPreventive) ? 90 : min;
}

// Default date for the booking modal.
//
// Cameron 2026-05-06: "Remove that gate. Book ASAP — every day a
// patient who just accepted sits unbooked, conversion drops. The
// 'within 2 weeks' offset was silently leaking acceptance velocity."
//
// The new model:
//   * No prior visit (visit 1, or any one-off) → default to TODAY.
//     The receptionist starts from real chair availability and picks
//     a slot from the chip grid. The previous "today + N days" offset
//     pushed bookings out for no operational reason.
//   * Prior visit set (visit 2+ in a multi-visit plan) → default to
//     priorVisitDate + daysOut, clamped to today minimum so we never
//     try to book yesterday. The biological gap between visit 1 and
//     visit 2 is the only legitimate use of the catalogue's
//     default_booking_window_days here.
//
// We no longer slide to Tue/Wed/Thu — clinic-closed days are surfaced
// by the chip grid's "Clinic is closed" banner, and biasing away from
// Mon/Fri caused front-desk friction when those WERE the open days.
function smartDefaultDate(daysOut, anchor = null) {
  const today = clinicTodayDateString();
  if (!anchor) {
    return clinicDateAt(today);
  }
  const anchorDate = clinicDateStringFromInstant(anchor);
  const base = addClinicDays(anchorDate, daysOut);
  return clinicDateAt(base && base >= today ? base : today);
}

function isoDate(d) {
  // "YYYY-MM-DD" — for <input type="date"> binding.
  return clinicDateStringFromInstant(d);
}

function formatRange(start, end) {
  if (!start) return "—";
  try {
    const s = new Date(start);
    const e = end ? new Date(end) : null;
    const date = s.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", timeZone: CLINIC_TIME_ZONE });
    const t1 = s.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit", timeZone: CLINIC_TIME_ZONE });
    const t2 = e ? e.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit", timeZone: CLINIC_TIME_ZONE }) : null;
    return t2 ? `${date}, ${t1}–${t2}` : `${date}, ${t1}`;
  } catch { return start; }
}

// Build the 15-minute slot grid for a single day, bounded by the
// clinic's operating hours for that day-of-week. The 12-1 PM lunch
// slot is rendered as its own group when it falls within open hours;
// chips remain clickable (some doctors work through lunch) and the
// dialog warns when a booking overlaps + suggests a shifted lunch.
//
// Operating hours come from get_appointment_settings → hours[dayKey]
// → { open: "09:00", close: "18:00", closed: false }. Without this
// guard, the dialog's hardcoded 8-18 window let receptionists book
// outside clinic hours — those appointments persisted but were
// invisible on the Calendar grid (whose rendering window IS the
// clinic's hours), so the booking looked "lost".
function buildSlotGrid(openMin, closeMin) {
  const morning = [];
  const lunch   = [];
  const afternoon = [];
  if (closeMin <= openMin) return { morning, lunch, afternoon };
  for (let total = openMin; total < closeMin; total += 15) {
    const h = Math.floor(total / 60);
    const m = total % 60;
    const slot = { h, m };
    if (h < 12) morning.push(slot);
    else if (h === 12) lunch.push(slot);
    else afternoon.push(slot);
  }
  return { morning, lunch, afternoon };
}

const DAY_KEYS = ["sunday","monday","tuesday","wednesday","thursday","friday","saturday"];
function parseHM(hm, fallback) {
  if (!hm) return fallback;
  const [h, m] = String(hm).split(":").map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return fallback;
  return h * 60 + m;
}
const FALLBACK_OPEN_MIN  = 9 * 60;   // 09:00 — matches the v1 default seed
const FALLBACK_CLOSE_MIN = 18 * 60;  // 18:00

// Lunch window in absolute minutes-of-day. 12:00 = 720, 13:00 = 780.
const LUNCH_START_MIN = 12 * 60;
const LUNCH_END_MIN   = 13 * 60;

// Format an absolute minutes-of-day as "12:30 PM" etc.
function fmtAbsMinutes(min) {
  const h = Math.floor(min / 60);
  const m = min % 60;
  const h12 = ((h + 11) % 12) + 1;
  const ap  = h < 12 ? "AM" : "PM";
  return `${h12}:${String(m).padStart(2, "0")} ${ap}`;
}

function fmtDuration(min) {
  const n = Number(min) || 0;
  if (n < 60) return `${n} min`;
  const h = Math.floor(n / 60);
  const m = n % 60;
  if (!m) return `${h} hr`;
  return `${h} hr ${m} min`;
}

function durationRecommendation(defaultMinutes, breakdown) {
  const recommended = Number(breakdown?.recommendedMinutes ?? defaultMinutes);
  return Number.isFinite(recommended) && recommended > 0 ? recommended : null;
}

function durationBreakdownLabel(breakdown) {
  const raw = Number(breakdown?.rawProcedureMinutes) || 0;
  const setup = Number(breakdown?.chairSetupMinutes) || 0;
  if (raw <= 0) return null;
  if (setup <= 0) return `${raw} min procedure`;
  return `${raw} min procedure + ${setup} min setup`;
}

function minutesBetween(start, end) {
  const s = new Date(start);
  const e = new Date(end);
  const diff = Math.round((e.getTime() - s.getTime()) / 60_000);
  return Number.isFinite(diff) && diff > 0 ? diff : null;
}

function slotFromIso(iso) {
  if (!iso) return null;
  const parts = clinicTimePartsFromInstant(iso);
  if (!parts) return null;
  return { h: parts.hour, m: parts.minute };
}

function fmtChipLabel(slot) {
  const h12 = ((slot.h + 11) % 12) + 1;
  const mm  = String(slot.m).padStart(2, "0");
  return `${h12}:${mm}`;
}

export default function BookFutureNeedDialog({
  open,
  onOpenChange,
  bundle,         // { procedure_name, tooth_number, surfaces: [...], needs: [...], estimatedMinutes, category, categories?, isAll? }
  patientId,
  patientName,
  navigate,       // unused now (kept for prop-compat)
  defaultDoctorId,
  defaultChairId,
  onBooked,       // (apptId, label) => void — called after successful create
  appointmentStatus = "Pencil", // 'Pencil' for Checkout (slot reservation, promoted at commit);
                                // 'Scheduled' for one-shot bookings like Tx Plan Center drawer.
  priorVisitDate = null, // Optional ISO timestamp of the patient's most recent
                         // completed visit on this plan. When set, the smart-
                         // default date becomes "priorVisitDate + urgencyDays"
                         // instead of "today + urgencyDays". Tx Plan Center's
                         // "Book Next Visit" passes this so visit 2+ default
                         // to a sensible follow-up window rather than urgency-
                         // from-today (which assumes a fresh proposal).
  hideHintForVisitOne = false, // When true, the "Usually within N weeks" hint
                               // is suppressed for visit-1 bookings (no
                               // priorVisitDate). Tx Plan Center sets this so
                               // a freshly-accepted plan doesn't anchor the
                               // receptionist's eye on a future date — book
                               // ASAP. Visit 2+ hints (with priorVisitDate)
                               // still render. Checkout's future-need flow
                               // keeps the default behavior.
  mustStartAfter = null,       // Hard chronology boundary for workflows where the
                               // new appointment must follow a specific visit.
                               // Unlike the clinical timing guidance above, slots
                               // at or before this instant are invalid.
  doctorRequestedDays = null,  // Doctor's chairside "come back within N days"
                               // request from Future Needs. When present, the
                               // dialog defaults to priorVisitDate + N days
                               // and warns if front desk books later.
  headingOverride = null,
  subtitleOverride = null,
  subtitleDurationContext = null,
  rescheduleAppointment = null,
  submitLabel = null,
  onRescheduled = null,
  appointmentSummary = null,
  appointmentInputExtra = null,
  initialNotes = "",
  demoMode = false,
  demoInitialDate = null,
  demoReferenceData = null,
  demoDayAppointments = EMPTY_DEMO_APPOINTMENTS,
  demoUpcomingAppointments = EMPTY_DEMO_APPOINTMENTS,
  demoAppointmentId = "tutorial-rct-booking",
}) {
  const { activeBranchId } = useBranch();
  const targets = useMemo(() => (bundle?.needs ?? []), [bundle]);
  const isReschedule = !!rescheduleAppointment?.id;
  const appointmentDurationMin = rescheduleAppointment
    ? minutesBetween(rescheduleAppointment.scheduled_start, rescheduleAppointment.scheduled_end)
    : null;
  const bundleDurationMin = bundle?.estimatedMinutes && bundle.estimatedMinutes > 0
    ? bundle.estimatedMinutes
    : null;
  const defaultDurationMin = bundleDurationMin || appointmentDurationMin || 60;
  const [durationMin, setDurationMin] = useState(defaultDurationMin);
  const recommendedDurationMin = durationRecommendation(defaultDurationMin, bundle?.durationBreakdown);
  const recommendedBreakdownLabel = durationBreakdownLabel(bundle?.durationBreakdown);

  // --- Smart default date -------------------------------------------
  const normalizedDoctorRequestedDays = useMemo(() => {
    const n = Number(doctorRequestedDays ?? followUpRequestedDays(bundle));
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [doctorRequestedDays, bundle]);

  const urgencyDays = useMemo(() => {
    if (normalizedDoctorRequestedDays != null) {
      return normalizedDoctorRequestedDays;
    }
    const cats = bundle?.isAll ? (bundle.categories || []) : (bundle?.category ? [bundle.category] : []);
    return pickUrgencyDays(cats);
  }, [bundle, normalizedDoctorRequestedDays]);

  const mustStartAfterMs = useMemo(() => {
    if (!mustStartAfter) return null;
    const parsed = new Date(mustStartAfter).getTime();
    return Number.isFinite(parsed) ? parsed : null;
  }, [mustStartAfter]);
  const minimumBookingDate = useMemo(() => {
    const today = clinicTodayDateString();
    if (mustStartAfterMs == null) return today;
    const boundaryDate = clinicDateStringFromInstant(new Date(mustStartAfterMs));
    return boundaryDate && boundaryDate > today ? boundaryDate : today;
  }, [mustStartAfterMs]);

  // --- Form state ---------------------------------------------------
  const [date, setDate]         = useState("");
  const [doctorId, setDoctorId] = useState("");
  const [chairId, setChairId]   = useState("");
  const [selectedSlot, setSelectedSlot] = useState(null); // { h, m } — start of the booking
  const [hoverSlot, setHoverSlot] = useState(null);       // { h, m } — preview only
  const [notes, setNotes]       = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);
  // Overlap is a warning, not a block: we detect conflicts for the chosen
  // slot and let the receptionist book anyway (create_appointment_allow_overlap).
  const [conflicts, setConflicts] = useState([]);

  // Reset on open with smart defaults applied
  useEffect(() => {
    if (!open) return;
    const appointmentStart = rescheduleAppointment?.scheduled_start
      ? new Date(rescheduleAppointment.scheduled_start)
      : null;
    const initialDate = demoInitialDate
      || (appointmentStart && !Number.isNaN(appointmentStart.getTime())
        ? isoDate(appointmentStart)
        : isoDate(smartDefaultDate(urgencyDays, priorVisitDate)));
    setDate(initialDate >= minimumBookingDate ? initialDate : minimumBookingDate);
    setDoctorId(rescheduleAppointment?.doctor_id || defaultDoctorId || "");
    setChairId(rescheduleAppointment?.chair_id || defaultChairId || "");
    setDurationMin(defaultDurationMin);
    setSelectedSlot(slotFromIso(rescheduleAppointment?.scheduled_start));
    setHoverSlot(null);
    setNotes(initialNotes || "");
    setSubmitting(false);
    setFormError(null);
  }, [open, urgencyDays, priorVisitDate, minimumBookingDate, defaultDoctorId, defaultChairId, initialNotes, defaultDurationMin, rescheduleAppointment, demoInitialDate]);

  // --- Reference data ----------------------------------------------
  const [doctors, setDoctors] = useState([]);
  const [chairs, setChairs]   = useState([]);
  const [settings, setSettings] = useState(null);
  useEffect(() => {
    if (!open) return;
    if (demoMode) {
      setDoctors(demoReferenceData?.doctors ?? [{ id: "tutorial-doctor", display_name: "Dr. Reyes" }]);
      setChairs(demoReferenceData?.chairs ?? [{ id: "tutorial-chair", name: "Chair 1" }]);
      setSettings(demoReferenceData?.settings ?? {
        hours: {
          sunday: { closed: true },
          monday: { open: "09:00", close: "18:00", closed: false },
          tuesday: { open: "09:00", close: "18:00", closed: false },
          wednesday: { open: "09:00", close: "18:00", closed: false },
          thursday: { open: "09:00", close: "18:00", closed: false },
          friday: { open: "09:00", close: "18:00", closed: false },
          saturday: { open: "09:00", close: "15:00", closed: false },
        },
      });
      return undefined;
    }
    let cancelled = false;
    // list_clinic_doctor_records returns public.doctors rows, which is
    // the FK shape appointments.doctor_id needs. The branch argument
    // keeps scheduling choices aligned with the active branch.
    Promise.all([
      supabase.rpc("list_clinic_doctor_records", { p_branch_id: activeBranchId || null }),
      supabase.rpc("list_clinic_chairs", { p_branch_id: activeBranchId || null }),
      supabase.rpc("get_appointment_settings"),
    ]).then(([d, c, s]) => {
      if (cancelled) return;
      setDoctors((d?.data ?? []).filter((x) => !x.archived));
      setChairs((c?.data ?? []).filter((x) => !x.archived));
      setSettings(s?.data ?? null);
    });
    return () => { cancelled = true; };
  }, [activeBranchId, open, demoMode, demoReferenceData]);

  // Once doctors/chairs lists load, auto-fill defaults so the receptionist
  // doesn't have to pick. Cases handled:
  //   * `defaultDoctorId` points to an archived doctor (e.g. plan was
  //     created under a member that's since been off-boarded) — the
  //     dropdown can't match that ID against its non-archived options,
  //     so we fall back to the first non-archived doctor instead of
  //     leaving the select stuck on "Unassigned".
  //   * `defaultChairId` is missing or archived — pick the clinic's
  //     first chair. `appointments.chair_id` is NOT NULL in the DB,
  //     so leaving the dropdown on "No chair" guarantees a constraint
  //     violation at book-time. We avoid that here.
  useEffect(() => {
    if (!open) return;
    if (doctors.length > 0) {
      const currentMatches = doctorId && doctors.find((d) => d.id === doctorId);
      if (!currentMatches) {
        const def = defaultDoctorId && doctors.find((d) => d.id === defaultDoctorId);
        setDoctorId(def ? defaultDoctorId : doctors[0].id);
      }
    }
    if (chairs.length > 0) {
      const currentMatches = chairId && chairs.find((c) => c.id === chairId);
      if (!currentMatches) {
        const def = defaultChairId && chairs.find((c) => c.id === defaultChairId);
        setChairId(def ? defaultChairId : chairs[0].id);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, doctors, chairs, doctorId, chairId]);

  // Operating hours for the SELECTED date's day-of-week. The chip
  // grid + close-time guard both read this. If settings haven't
  // loaded yet (or the day is closed), fall back to a 9-18 window
  // so the form still renders something during the loading frame.
  const dayConfig = useMemo(() => {
    if (!date) return { closed: false, openMin: FALLBACK_OPEN_MIN, closeMin: FALLBACK_CLOSE_MIN };
    const dayIndex = clinicDayIndex(date);
    const dayKey = DAY_KEYS[dayIndex ?? 0];
    const dayHours = settings?.hours?.[dayKey];
    if (dayHours?.closed) return { closed: true, openMin: 0, closeMin: 0 };
    return {
      closed: false,
      openMin:  parseHM(dayHours?.open,  FALLBACK_OPEN_MIN),
      closeMin: parseHM(dayHours?.close, FALLBACK_CLOSE_MIN),
    };
  }, [date, settings]);

  // --- Existing upcoming appointments (secondary path) -------------
  const [upcomingAppts, setUpcomingAppts] = useState([]);
  useEffect(() => {
    if (!open || !patientId || isReschedule) {
      setUpcomingAppts([]);
      return;
    }
    if (demoMode) {
      setUpcomingAppts(demoUpcomingAppointments);
      return undefined;
    }
    let cancelled = false;
    const nowMs = Date.now();
    const lowerBoundMs = mustStartAfterMs == null ? nowMs : Math.max(nowMs, mustStartAfterMs);
    const lowerBoundIso = new Date(lowerBoundMs).toISOString();
    supabase
      .from("appointments")
      .select("id, scheduled_start, scheduled_end, status, notes")
      .eq("patient_id", patientId)
      .in("status", ELIGIBLE_STATUSES)
      .gte("scheduled_start", lowerBoundIso)
      .order("scheduled_start", { ascending: true })
      .limit(20)
      .then(({ data }) => {
        if (cancelled) return;
        setUpcomingAppts((data ?? []).filter((appointment) => {
          if (mustStartAfterMs == null) return true;
          return new Date(appointment.scheduled_start).getTime() > mustStartAfterMs;
        }));
      });
    return () => { cancelled = true; };
  }, [open, patientId, isReschedule, demoMode, demoUpcomingAppointments, mustStartAfterMs]);

  // --- Day's appointments for chip availability -------------------
  // Refetched whenever date changes. The slot map needs the whole day's
  // active schedule so it can block either the selected chair OR the
  // selected doctor, even when the doctor's conflict is in another chair.
  const [dayAppts, setDayAppts] = useState([]);
  const [dayLoading, setDayLoading] = useState(false);
  useEffect(() => {
    if (!open || !date) { setDayAppts([]); return; }
    if (demoMode) {
      setDayAppts(demoDayAppointments);
      setDayLoading(false);
      return undefined;
    }
    let cancelled = false;
    setDayLoading(true);
    const from = clinicDateAt(date, 0, 0, 0).toISOString();
    const to   = clinicDateAt(date, 23, 59, 59).toISOString();
    supabase.rpc("list_appointments", {
      p_from: from, p_to: to,
      p_doctor_id: null,
      p_chair_id:  null,
      p_branch_id: activeBranchId || null,
    }).then(({ data }) => {
      if (cancelled) return;
      setDayAppts((data ?? []).filter(
        (a) => a.id !== rescheduleAppointment?.id
          && isBlockingAppointmentStatus(a.status)
      ));
      setDayLoading(false);
    });
    return () => { cancelled = true; };
  }, [activeBranchId, open, date, rescheduleAppointment?.id, demoMode, demoDayAppointments]);

  // --- Slot availability for chips --------------------------------
  // For each 15-min start, check if [start, start+duration] would
  // overlap any non-cancelled day-appointment. Result map keyed by
  // "h:m". Lunch chips are NOT auto-blocked here — they show a warning
  // instead, since some doctors work through lunch.
  const slotAvailability = useMemo(() => {
    if (!date || dayConfig.closed) return {};
    const map = {};
    const { morning, lunch, afternoon } = buildSlotGrid(dayConfig.openMin, dayConfig.closeMin);
    const all = [...morning, ...lunch, ...afternoon];
    for (const slot of all) {
      const start = clinicDateAtMinutes(date, slot.h * 60 + slot.m);
      const end   = new Date(start.getTime() + durationMin * 60_000);
      let state = "free";
      if (mustStartAfterMs != null && start.getTime() <= mustStartAfterMs) {
        state = "before_required_start";
      }
      for (const a of dayAppts) {
        const aStart = new Date(a.scheduled_start);
        const aEnd   = new Date(a.scheduled_end);
        if (start < aEnd && end > aStart) {
          if (doctorId && a.doctor_id === doctorId) {
            state = "doctor_busy";
            break;
          }
          if (chairId && a.chair_id === chairId) {
            state = "chair_busy";
            break;
          }
          if (!doctorId && !chairId) {
            state = "busy";
            break;
          }
        }
      }
      // Block if end runs past clinic close (so the booking would
      // disappear off the bottom of the Calendar grid).
      const endMinOfDay = slot.h * 60 + slot.m + durationMin;
      if (endMinOfDay > dayConfig.closeMin) state = "busy";
      map[`${slot.h}:${slot.m}`] = state;
    }
    return map;
  }, [date, dayAppts, durationMin, dayConfig, doctorId, chairId, mustStartAfterMs]);

  const grid = useMemo(
    () => buildSlotGrid(dayConfig.openMin, dayConfig.closeMin),
    [dayConfig.openMin, dayConfig.closeMin],
  );

  const doctorWindowNotice = useMemo(() => {
    if (!normalizedDoctorRequestedDays || !priorVisitDate || !date) return null;
    const priorDate = clinicDateStringFromInstant(priorVisitDate);
    if (!priorDate) return null;
    const targetDate = addClinicDays(priorDate, normalizedDoctorRequestedDays);
    const target = clinicDateAt(targetDate, 12);
    const picked = clinicDateAt(date, 12);
    const daysLate = picked > target
      ? Math.round((picked.getTime() - target.getTime()) / 86400000)
      : 0;
    return {
      days: normalizedDoctorRequestedDays,
      daysLate,
      targetLabel: target.toLocaleDateString(undefined, {
        weekday: "short", month: "short", day: "numeric", timeZone: CLINIC_TIME_ZONE,
      }),
      priorLabel: clinicDateAt(priorDate, 12).toLocaleDateString(undefined, {
        month: "short", day: "numeric", timeZone: CLINIC_TIME_ZONE,
      }),
    };
  }, [normalizedDoctorRequestedDays, priorVisitDate, date]);

  // --- Smart lunch overlap check + suggested shifted lunch ---------
  // If the selected block intersects [12:00, 13:00), compute a
  // proposed shifted-lunch window so we can show the front desk
  // exactly what trade-off they're making. The shift target is the
  // first 1-hour gap after the booking ends within the work day,
  // skipping any conflict with day-appts.
  const lunchOverlap = useMemo(() => {
    if (!selectedSlot) return null;
    const startMin = selectedSlot.h * 60 + selectedSlot.m;
    const endMin   = startMin + durationMin;
    if (endMin <= LUNCH_START_MIN || startMin >= LUNCH_END_MIN) return null;
    // Compute the next free 1-hour window after the booking ends,
    // bounded by the clinic's actual close time for this day.
    const dayCloseMin = dayConfig.closeMin;
    let candidate = endMin;
    function isFree(s) {
      if (!date) return true;
      const e = s + 60;
      if (e > dayCloseMin) return false;
      const startD = clinicDateAtMinutes(date, s);
      const endD = new Date(startD.getTime() + 60 * 60_000);
      // Also exclude the user's own selected booking range
      const userStart = clinicDateAtMinutes(date, startMin);
      const userEnd = new Date(userStart.getTime() + durationMin * 60_000);
      if (startD < userEnd && endD > userStart) return false;
      for (const a of dayAppts) {
        const aS = new Date(a.scheduled_start);
        const aE = new Date(a.scheduled_end);
        if (startD < aE && endD > aS) return false;
      }
      return true;
    }
    while (candidate + 60 <= dayCloseMin && !isFree(candidate)) {
      candidate += 15;
    }
    if (candidate + 60 > dayCloseMin) {
      return { suggested: null };
    }
    return { suggested: { start: candidate, end: candidate + 60 } };
  }, [selectedSlot, durationMin, dayAppts, date, dayConfig]);

  // Detect (don't block) overlapping appointments for the chosen slot so
  // the receptionist gets a specific heads-up before booking over them.
  useEffect(() => {
    if (demoMode || isReschedule || !selectedSlot || !date || durationMin <= 0) { setConflicts([]); return; }
    if (!doctorId && !chairId) { setConflicts([]); return; }
    let cancelled = false;
    const start = clinicDateAtMinutes(date, selectedSlot.h * 60 + selectedSlot.m);
    const end   = new Date(start.getTime() + durationMin * 60_000);
    supabase.rpc("check_appointment_conflicts", {
      p_doctor_id:  doctorId || null,
      p_chair_id:   chairId || null,
      p_start:      start.toISOString(),
      p_end:        end.toISOString(),
      p_exclude_id: rescheduleAppointment?.id || null,
    }).then(({ data, error }) => {
      if (cancelled || error) return;
      setConflicts(Array.isArray(data) ? data : []);
    });
    return () => { cancelled = true; };
  }, [demoMode, isReschedule, selectedSlot, date, durationMin, doctorId, chairId, rescheduleAppointment?.id]);

  // --- Submit ----------------------------------------------
  async function submitBooking() {
    if (!selectedSlot || !date || !patientId) return;
    const selectedState = slotAvailability[`${selectedSlot.h}:${selectedSlot.m}`] || "free";
    if (slotUnavailable(selectedState)) {
      const message = slotLabel(selectedState) || "This slot is unavailable.";
      setFormError(message);
      return;
    }
    setSubmitting(true);
    setFormError(null);
    const start = clinicDateAtMinutes(date, selectedSlot.h * 60 + selectedSlot.m);
    const end   = new Date(start.getTime() + durationMin * 60_000);
    if (mustStartAfterMs != null && start.getTime() <= mustStartAfterMs) {
      setSubmitting(false);
      setFormError("The next appointment must be after the current visit.");
      return;
    }
    if (demoMode) {
      const label = formatRange(start.toISOString(), end.toISOString());
      setSubmitting(false);
      onBooked?.(demoAppointmentId, label);
      onOpenChange(false);
      return;
    }
    if (isReschedule) {
      const { error } = await supabase.rpc("reschedule_appointment", {
        p_appointment_id: rescheduleAppointment.id,
        p_new_start:     start.toISOString(),
        p_new_end:       end.toISOString(),
        p_new_doctor_id: doctorId || null,
        p_new_chair_id:  chairId || null,
        p_reason:        notes.trim() || null,
      });
      setSubmitting(false);
      if (error) {
        const message = error.message ?? "Could not change booking.";
        setFormError(message);
        toast.error(message);
        return;
      }
      const label = formatRange(start.toISOString(), end.toISOString());
      toast.success(`Booking changed: ${label}.`);
      onRescheduled?.({
        appointment_id: rescheduleAppointment.id,
        appointment_label: label,
        scheduled_start: start.toISOString(),
        scheduled_end: end.toISOString(),
      });
      onOpenChange(false);
      return;
    }

    // Default status is 'Pencil' — slot reservation only, promoted to
    // Scheduled atomically by commit_checkout_with_followup when the
    // receptionist hits "Complete checkout". For one-shot bookings
    // (Tx Plan Center drawer's "Book First Visit") the caller passes
    // appointmentStatus="Scheduled" so the row is permanent immediately.
    const extra = appointmentInputExtra && typeof appointmentInputExtra === "object"
      ? appointmentInputExtra
      : {};
    // Overlap is a warning, not a wall: when the chosen slot conflicts,
    // book through the explicit override wrapper so the guard steps aside
    // for this one booking (protection stays on everywhere else).
    const bookRpc = conflicts.length > 0 ? "create_appointment_allow_overlap" : "create_appointment";
    const { data, error } = await supabase.rpc(bookRpc, {
      p_input: {
        ...extra,
        patient_id:      patientId,
        doctor_id:       doctorId || null,
        chair_id:        chairId  || null,
        scheduled_start: start.toISOString(),
        scheduled_end:   end.toISOString(),
        status:          appointmentStatus,
        notes:           notes.trim() || extra.notes || null,
      },
    });
    setSubmitting(false);
    // Two failure modes worth handling:
    //   (a) The RPC raised inside Postgres → row never created, error
    //       set, no cleanup needed.
    //   (b) The RPC succeeded but the response was truncated /
    //       network dropped on the way back → error AND a partial
    //       data with an id (rare, but real). If we got an id back,
    //       fire-and-forget cancel_pencil_appointment so the row
    //       doesn't ghost the calendar. The 24h orphan-Pencil cron
    //       job is the long-tail backstop for the case where we
    //       didn't even get the id back.
    const partialId = data?.id || (Array.isArray(data) ? data[0]?.id : null);
    if (error) {
      if (partialId) {
        supabase
          .rpc("cancel_pencil_appointment", { p_appointment_id: partialId })
          .catch(() => { /* best-effort; cron job catches it if this fails too */ });
      }
      const message = error.message ?? "Could not create appointment.";
      setFormError(message);
      toast.error(error.message ?? "Could not create appointment.", {
        description: "If a slot got reserved despite this error, it'll auto-release.",
      });
      return;
    }
    const apptId = partialId;
    const label  = formatRange(start.toISOString(), end.toISOString());
    if (!apptId) {
      toast.error("Appointment created but no id returned. Refresh and try again.");
      return;
    }
    toast.success(`Appointment created: ${label}.`);
    onBooked?.(apptId, label);
  }

  // Stage-only path for the "Use existing" list — same callback, no DB work.
  function pickExisting(apptId) {
    const a = upcomingAppts.find((x) => x.id === apptId);
    if (a && mustStartAfterMs != null && new Date(a.scheduled_start).getTime() <= mustStartAfterMs) {
      setFormError("The next appointment must be after the current visit.");
      return;
    }
    const label = a ? formatRange(a.scheduled_start, a.scheduled_end) : null;
    onBooked?.(apptId, label);
    onOpenChange(false);
  }

  function shiftDate(deltaDays) {
    if (!date) return;
    const shifted = addClinicDays(date, deltaDays);
    setDate(shifted < minimumBookingDate ? minimumBookingDate : shifted);
    setSelectedSlot(null);
    setFormError(null);
  }

  function pickDuration(nextMinutes) {
    const minutes = Number(nextMinutes);
    setDurationMin(Number.isFinite(minutes) ? Math.max(0, Math.round(minutes)) : 0);
    setSelectedSlot(null);
    setHoverSlot(null);
    setFormError(null);
  }

  if (!open) return null;

  const isAll = !!bundle?.isAll;
  const heading = headingOverride || followUpBookingHeading(bundle);

  const subtitle = subtitleDurationContext
    ? `${subtitleDurationContext} · ~${fmtDuration(durationMin)}`
    : subtitleOverride ?? (bundle
    ? (() => {
        const dur = `~${fmtDuration(durationMin)}`;
        if (isAll) return `${dur} estimated chair time`;
        const tooth = bundle.tooth_number ? ` · #${bundle.tooth_number}` : "";
        const surf  = (bundle.surfaces ?? []).filter(Boolean);
        const surfStr = surf.length > 0 ? ` (${surf.join(", ")})` : "";
        return `${bundle.procedure_name}${tooth}${surfStr} · ${dur}`;
      })()
    : "");

  // Tiny clinical-norm subtitle. Demoted from the boxed "Suggested:
  // within 2 weeks" callout — that was driving the receptionist's
  // default behavior toward late bookings (every day unbooked = lost
  // conversion). Now it's just an informational label so a new hire
  // still sees the typical clinical window without it nudging the
  // date picker.
  const urgencyHint = (() => {
    if (!priorVisitDate) {
      // Tx Plan Center suppresses the visit-1 hint entirely — for a
      // freshly-accepted plan, the right move is "book ASAP", not
      // "wait 2 weeks per the category default". Hint-driven defaults
      // silently leak conversion velocity.
      if (hideHintForVisitOne) return null;
      const base = bookingWindowHint(urgencyDays);
      return base ? base.replace(/^Suggested:\s*/i, "Usually ") : null;
    }
    const priorDate = clinicDateStringFromInstant(priorVisitDate);
    if (!priorDate) return null;
    const priorLabel = clinicDateAt(priorDate, 12).toLocaleDateString(undefined, {
      month: "short", day: "numeric", timeZone: CLINIC_TIME_ZONE,
    });
    return `Last visit was ${priorLabel} · usually ~${urgencyDays} day${urgencyDays === 1 ? "" : "s"} after.`;
  })();

  // Determines whether a chip falls inside the duration block of an
  // anchor slot. e.g. anchor=8:00 + duration=60 → covers 8:00, 8:15,
  // 8:30, 8:45 (4 chips). Used to "fill in" every chip the booking
  // actually consumes, not just the start.
  function chipMinutes(slot)  { return slot.h * 60 + slot.m; }
  function chipFallsInBlock(slot, anchor) {
    if (!anchor) return false;
    const a = chipMinutes(anchor);
    const c = chipMinutes(slot);
    return c >= a && c < a + durationMin;
  }

  function chipClass(state, slot, opts = {}) {
    const inSelected = chipFallsInBlock(slot, selectedSlot);
    const inHover    = !inSelected && chipFallsInBlock(slot, hoverSlot);
    if (state !== "free") return "bg-slate-100 text-slate-400 border-slate-200 line-through cursor-not-allowed";
    if (inSelected) {
      const isLead = selectedSlot && slot.h === selectedSlot.h && slot.m === selectedSlot.m;
      return isLead
        ? "bg-teal-600 text-white border-teal-600"
        : "bg-teal-100 text-teal-800 border-teal-300";
    }
    if (inHover) return "bg-teal-50 text-teal-700 border-teal-200";
    // Lunch chips get a soft amber tint so the carve-out is visible
    // even though the chip is clickable.
    if (opts.isLunch) return "bg-amber-50 text-amber-800 border-amber-200 hover:border-amber-400 hover:bg-amber-100";
    return "bg-white text-slate-700 border-slate-200 hover:border-teal-400";
  }

  const close = () => { if (!submitting) onOpenChange(false); };
  const slotUnavailable = (state) => state !== "free";
  const slotLabel = (state) => {
    if (state === "before_required_start") return "Must be after current visit";
    if (state === "doctor_busy") return "Doctor Unavailable";
    if (state === "chair_busy") return "Chair Unavailable";
    if (state === "busy") return "Unavailable";
    return null;
  };
  const selectedLabel = selectedSlot
    ? `${clinicDateAt(date, 12).toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric", timeZone: CLINIC_TIME_ZONE })}, ${fmtChipLabel(selectedSlot)} ${selectedSlot.h < 12 ? "AM" : "PM"}`
    : null;
  const selectedSlotState = selectedSlot
    ? (slotAvailability[`${selectedSlot.h}:${selectedSlot.m}`] || "free")
    : "free";

  return (
    <div role="dialog" aria-modal="true"
      data-testid="book-future-need-dialog"
      data-tutorial-id="book-future-need-dialog"
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4"
      onMouseDown={(e) => { if (e.target === e.currentTarget) close(); }}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[92vh]">
        <div className="px-6 py-4 border-b border-slate-100 flex items-start justify-between gap-3 shrink-0">
          <div className="min-w-0">
            <h2 className="text-base font-bold text-slate-900">{heading}</h2>
            <p className="text-xs text-slate-500 mt-0.5 truncate">
              <span className="text-slate-700 font-medium">{patientName}</span>
              {subtitle ? <> · {subtitle}</> : null}
            </p>
          </div>
          <button type="button" onClick={close} disabled={submitting}
            data-testid="book-future-need-close"
            className="p-1 rounded hover:bg-slate-100 text-slate-500 disabled:opacity-40">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-6 py-4 overflow-y-auto flex-1 space-y-4">
          {appointmentSummary ? (
            <div data-testid="book-appointment-summary">
              {appointmentSummary}
            </div>
          ) : null}

          {/* Date / Doctor / Chair pickers */}
          <div className="grid sm:grid-cols-[1fr_auto_1fr_1fr] gap-2 items-end">
            <div>
              <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500" htmlFor="book-date">Date</label>
              <input id="book-date" type="date" value={date}
                onChange={(e) => { setDate(e.target.value); setSelectedSlot(null); setFormError(null); }}
                min={minimumBookingDate}
                data-testid="book-date"
                data-tutorial-id="book-date-input"
                className="mt-1 w-full px-2 py-1.5 text-sm rounded border border-slate-200 focus:border-teal-500 outline-none" />
            </div>
            <div className="flex items-end gap-1 pb-0.5">
              <button type="button" onClick={() => shiftDate(-1)}
                disabled={date <= minimumBookingDate}
                title="Previous day"
                data-tutorial-id="book-date-previous"
                className="p-1.5 rounded border border-slate-200 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40">
                <ChevronLeft className="w-4 h-4 text-slate-600" />
              </button>
              <button type="button" onClick={() => shiftDate(1)}
                title="Next day"
                data-tutorial-id="book-date-next"
                className="p-1.5 rounded border border-slate-200 hover:bg-slate-50">
                <ChevronRight className="w-4 h-4 text-slate-600" />
              </button>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500" htmlFor="book-doctor">
                <Stethoscope className="w-3 h-3 inline mr-1" />Doctor
              </label>
              <select id="book-doctor" value={doctorId}
                onChange={(e) => { setDoctorId(e.target.value); setSelectedSlot(null); setFormError(null); }}
                data-testid="book-doctor"
                className="mt-1 w-full px-2 py-1.5 text-sm rounded border border-slate-200 focus:border-teal-500 outline-none">
                <option value="">Unassigned</option>
                {doctors.map((d) => (
                  <option key={d.id} value={d.id}>{formatDoctorName(d)}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500" htmlFor="book-chair">
                <Armchair className="w-3 h-3 inline mr-1" />Chair
              </label>
              <select id="book-chair" value={chairId}
                onChange={(e) => { setChairId(e.target.value); setSelectedSlot(null); setFormError(null); }}
                data-testid="book-chair"
                className="mt-1 w-full px-2 py-1.5 text-sm rounded border border-slate-200 focus:border-teal-500 outline-none">
                <option value="">No chair</option>
                {chairs.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label htmlFor="book-duration" className="block text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-1.5">
              <Clock className="w-3 h-3 inline mr-1" />Duration
            </label>
            <div className="relative w-full sm:max-w-[12rem]">
              <input
                id="book-duration"
                type="number"
                min="5"
                step="5"
                inputMode="numeric"
                value={durationMin}
                onChange={(e) => pickDuration(e.target.value)}
                data-testid="book-duration"
                className="w-full rounded border border-slate-200 bg-white px-2.5 py-1.5 pr-12 text-sm tabular-nums text-slate-800 outline-none focus:border-teal-500"
              />
              <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-xs font-semibold text-slate-400">
                mins
              </span>
            </div>
            <p className="mt-1 text-[10px] font-medium text-slate-500">
              60 mins = 1 hour
            </p>
            {recommendedDurationMin ? (
              <p className="mt-1.5 text-[11px] font-medium text-emerald-700" data-testid="book-duration-recommendation">
                Recommended: {fmtDuration(recommendedDurationMin)}
                {recommendedBreakdownLabel ? ` (${recommendedBreakdownLabel})` : ""}
              </p>
            ) : null}
          </div>

          {urgencyHint ? (
            <p className="text-[11px] text-slate-500 -mt-1">{urgencyHint}</p>
          ) : null}

          {doctorWindowNotice ? (
            <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-900 flex items-start gap-2"
                 data-testid="book-doctor-window-notice">
              <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="font-semibold">
                  Doctor asked for this patient to be back within {describeFollowUpDays(doctorWindowNotice.days)} of this appointment ({doctorWindowNotice.priorLabel}).
                </p>
                <p className="mt-0.5">
                  Target date: <strong>{doctorWindowNotice.targetLabel}</strong>.
                  {doctorWindowNotice.daysLate > 0 ? (
                    <> This selected date is <strong>{doctorWindowNotice.daysLate} day{doctorWindowNotice.daysLate === 1 ? "" : "s"} later</strong> than requested.</>
                  ) : (
                    <> This selected date fits the doctor's request.</>
                  )}
                </p>
              </div>
            </div>
          ) : null}

          {/* Overlap soft warning — the slot double-books a doctor or
              chair. Not a wall: the Book button stays enabled and routes
              through the allow-overlap wrapper. */}
          {conflicts.length > 0 ? (
            <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-900 flex items-start gap-2"
                 data-testid="book-overlap-warning">
              <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="font-semibold">
                  This time overlaps {conflicts.length === 1 ? "an existing appointment" : `${conflicts.length} existing appointments`}.
                </p>
                <ul className="mt-0.5 space-y-0.5">
                  {conflicts.slice(0, 4).map((c) => (
                    <li key={`${c.kind}-${c.appointment_id}`}>
                      <span className="font-medium">{c.party_name}</span>
                      {c.patient_name ? <> · {c.patient_name}</> : null}
                      {" · "}{formatRange(c.scheduled_start, c.scheduled_end)}
                    </li>
                  ))}
                </ul>
                <p className="mt-0.5 text-amber-800/70">You can still book — just double-check it's intentional.</p>
              </div>
            </div>
          ) : null}

          {/* Clinic-closed banner — shown when the picked date falls
              on a day the clinic is closed (per Settings → Hours). No
              chips render in this case; receptionist must shift dates. */}
          {dayConfig.closed ? (
            <div className="bg-rose-50 border border-rose-200 rounded-lg px-3 py-2 text-xs text-rose-900 flex items-start gap-2"
                 data-testid="book-clinic-closed"
                 data-tutorial-id="book-clinic-closed">
              <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
              <p>Clinic is closed on {clinicDateAt(date, 12).toLocaleDateString(undefined, { weekday: "long", timeZone: CLINIC_TIME_ZONE })}. Pick a different date.</p>
            </div>
          ) : null}

          {/* Time chip grid */}
          {!dayConfig.closed ? (
          <div data-testid="book-time-chips">
            <div className="flex items-center gap-3 mb-2">
              <p className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">Pick a time</p>
              {dayLoading ? <Loader2 className="w-3 h-3 animate-spin text-slate-400" /> : null}
              <p className="ml-auto text-[10px] text-slate-400">
                Clinic open <span className="font-semibold text-slate-600">{fmtAbsMinutes(dayConfig.openMin)}–{fmtAbsMinutes(dayConfig.closeMin)}</span>
                {" · "}each slot holds <span className="font-semibold text-slate-600">{durationMin} min</span>
              </p>
            </div>
            {grid.morning.length > 0 ? (
            <>
            <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 mb-1.5">Morning</p>
            <div className="flex flex-wrap gap-1 mb-3"
                 onMouseLeave={() => setHoverSlot(null)}>
              {grid.morning.map((slot) => {
                const k = `${slot.h}:${slot.m}`;
                const state = slotAvailability[k] || "free";
                return (
                  <button key={k} type="button"
                    disabled={slotUnavailable(state)}
                    onClick={() => { setSelectedSlot(slot); setFormError(null); }}
                    onMouseEnter={() => state === "free" ? setHoverSlot(slot) : null}
                    data-testid={`book-slot-${k}`}
                    data-tutorial-id={tutorialSlotTargetId(slot)}
                    className={`min-w-[3.4rem] text-[11px] font-semibold tabular-nums px-2 py-1 rounded border transition ${chipClass(state, slot)}`}>
                    {fmtChipLabel(slot)}
                    {slotLabel(state) ? <span className="sr-only"> {slotLabel(state)}</span> : null}
                  </button>
                );
              })}
            </div>
            </>
            ) : null}
            {grid.lunch.length > 0 ? (
            <>
            <p className="text-[10px] text-slate-400 mb-1.5 flex items-center gap-1.5">
              <span className="font-semibold uppercase tracking-wider text-amber-700">🍴 Lunch</span>
              <span>· 12-1, but clickable — some doctors work through</span>
            </p>
            <div className="flex flex-wrap gap-1 mb-3"
                 onMouseLeave={() => setHoverSlot(null)}>
              {grid.lunch.map((slot) => {
                const k = `${slot.h}:${slot.m}`;
                const state = slotAvailability[k] || "free";
                return (
                  <button key={k} type="button"
                    disabled={slotUnavailable(state)}
                    onClick={() => { setSelectedSlot(slot); setFormError(null); }}
                    onMouseEnter={() => state === "free" ? setHoverSlot(slot) : null}
                    data-testid={`book-slot-${k}`}
                    data-tutorial-id={tutorialSlotTargetId(slot)}
                    className={`min-w-[3.4rem] text-[11px] font-semibold tabular-nums px-2 py-1 rounded border transition ${chipClass(state, slot, { isLunch: true })}`}>
                    {fmtChipLabel(slot)}
                    {slotLabel(state) ? <span className="sr-only"> {slotLabel(state)}</span> : null}
                  </button>
                );
              })}
            </div>
            </>
            ) : null}
            {grid.afternoon.length > 0 ? (
            <>
            <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 mb-1.5">Afternoon</p>
            <div className="flex flex-wrap gap-1"
                 onMouseLeave={() => setHoverSlot(null)}>
              {grid.afternoon.map((slot) => {
                const k = `${slot.h}:${slot.m}`;
                const state = slotAvailability[k] || "free";
                return (
                  <button key={k} type="button"
                    disabled={slotUnavailable(state)}
                    onClick={() => { setSelectedSlot(slot); setFormError(null); }}
                    onMouseEnter={() => state === "free" ? setHoverSlot(slot) : null}
                    data-testid={`book-slot-${k}`}
                    data-tutorial-id={tutorialSlotTargetId(slot)}
                    className={`min-w-[3.4rem] text-[11px] font-semibold tabular-nums px-2 py-1 rounded border transition ${chipClass(state, slot)}`}>
                    {fmtChipLabel(slot)}
                    {slotLabel(state) ? <span className="sr-only"> {slotLabel(state)}</span> : null}
                  </button>
                );
              })}
            </div>
            </>
            ) : null}

            {/* Smart-lunch warning banner — shows only when the
                selected block crosses 12:00–13:00. Lets the booking
                proceed but flags the trade and suggests a shifted
                lunch window the doctor can take instead. */}
            {lunchOverlap ? (
              <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-900 flex items-start gap-2"
                   data-testid="book-lunch-warning">
                <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="font-semibold">This booking runs through the usual lunch (12:00–1:00).</p>
                  {lunchOverlap.suggested ? (
                    <p className="mt-0.5">
                      Suggested shift: lunch moves to <strong>{fmtAbsMinutes(lunchOverlap.suggested.start)}–{fmtAbsMinutes(lunchOverlap.suggested.end)}</strong>.
                    </p>
                  ) : (
                    <p className="mt-0.5">No 1-hour gap left in the day for a shifted lunch — Dr. would skip lunch today.</p>
                  )}
                  <p className="mt-0.5 text-amber-800/70">Click "Book this slot" anyway if the doctor's good with it.</p>
                </div>
              </div>
            ) : null}

            <div className="flex items-center gap-3 mt-2 text-[10px] text-slate-500 flex-wrap">
              <span className="inline-flex items-center gap-1">
                <span className="inline-block w-2.5 h-2.5 rounded bg-white border border-slate-200" />Free
              </span>
              <span className="inline-flex items-center gap-1">
                <span className="inline-block w-2.5 h-2.5 rounded bg-amber-50 border border-amber-200" />Lunch
              </span>
              <span className="inline-flex items-center gap-1">
                <span className="inline-block w-2.5 h-2.5 rounded bg-teal-600" />
                <span className="inline-block w-2.5 h-2.5 rounded bg-teal-100 border border-teal-300" />
                Selected ({durationMin}-min block)
              </span>
              <span className="inline-flex items-center gap-1">
                <span className="inline-block w-2.5 h-2.5 rounded bg-slate-100 border border-slate-200" />Not available
              </span>
            </div>
          </div>
          ) : null}

          {/* Notes */}
          <div>
            <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500" htmlFor="book-notes">
              Notes (optional)
            </label>
            <input id="book-notes" type="text" value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. Composite restoration follow-up"
              data-testid="book-notes"
              className="mt-1 w-full px-2 py-1.5 text-sm rounded border border-slate-200 focus:border-teal-500 outline-none" />
          </div>

          {formError ? (
            <div
              className="flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-semibold text-rose-700"
              data-testid="book-form-error"
            >
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>{formError}</span>
            </div>
          ) : null}

          {/* Existing-appointment fallback */}
          {upcomingAppts.length > 0 ? (
            <div className="border-t border-slate-100 pt-3">
              <p className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-2">
                Or attach to an upcoming appointment
              </p>
              <ul className="space-y-1">
                {upcomingAppts.map((a) => (
                  <li key={a.id}>
                    <button type="button" onClick={() => pickExisting(a.id)}
                      disabled={submitting}
                      data-testid={`book-future-need-pick-${a.id}`}
                      className="w-full text-left px-3 py-2 hover:bg-slate-50 rounded-lg flex items-center gap-3 disabled:opacity-50">
                      <CalendarIcon className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                      <span className="flex-1 text-xs text-slate-700">
                        {formatRange(a.scheduled_start, a.scheduled_end)}
                        <span className="text-slate-400 ml-2">· {a.status}</span>
                      </span>
                      <ArrowRight className="w-3.5 h-3.5 text-slate-300" />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>

        {/* Sticky footer with selected summary + Book button */}
        <div className="px-6 py-3 border-t border-slate-100 bg-slate-50/60 flex items-center gap-3 shrink-0">
          <span className="text-xs text-slate-700 truncate">
            {selectedLabel
              ? <><Check className="w-3 h-3 inline text-emerald-600 mr-1" /><strong>{selectedLabel}</strong> · {durationMin} min</>
              : <span className="text-slate-500"><AlertTriangle className="w-3 h-3 inline mr-1 text-amber-600" />Pick a time slot above to book.</span>}
          </span>
          <button type="button" onClick={close} disabled={submitting}
            data-testid="book-future-need-cancel"
            className="ml-auto text-xs font-semibold text-slate-600 hover:text-slate-900 px-3 py-1.5 rounded disabled:opacity-40">
            Cancel
          </button>
          <button type="button" onClick={submitBooking}
            disabled={!selectedSlot || submitting || dayConfig.closed || slotUnavailable(selectedSlotState)}
            data-testid="book-future-need-submit"
            data-tutorial-id="book-future-need-submit"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-teal-600 hover:bg-teal-700 rounded disabled:opacity-50">
            {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CalendarIcon className="w-3.5 h-3.5" />}
            {submitLabel || (isReschedule ? "Save booking" : "Book this slot")}
          </button>
        </div>
      </div>
    </div>
  );
}
